import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { TaskNoteDto } from '@dispatch/shared/services';

export interface NoteListItemProps {
  note: TaskNoteDto;
}

const formatTimestamp = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

/**
 * NoteListItem - renders a single task note row with author, timestamp, and
 * an optional "System" chip marking notes authored by a system actor
 * (`authorType === 'system'`, or the legacy `authoredBy === 'System'` string
 * for historical notes that predate the `authorType` discriminator). Any
 * `authorType` value other than the literal `'system'` is treated as a user
 * note by design - an unrecognized future value falls to the user arm rather
 * than the system one.
 *
 * The author line is suppressed for system notes (`!isSystem`): today every
 * system note's `authoredBy` is the literal string "System" (see
 * PersistSystemNoteAsync in NoteService), so rendering it as free text would
 * just duplicate the chip's label on the same row. This intentionally
 * differs from NotesPanel/AddNoteModal, which have no chip to fall back on
 * and so render "Unknown" rather than omitting the field entirely.
 */
export const NoteListItem: React.FC<NoteListItemProps> = ({ note }) => {
  const isSystem = note.authorType
    ? note.authorType === 'system'
    : note.authoredBy === 'System';
  const authorName = note.authoredBy || note.authoredByUserId;

  return (
    <Box
      data-testid={`note-list-item-${note.noteId}`}
      sx={{
        padding: '12px 16px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ marginBottom: '4px' }}
      >
        {note.categoryName && (
          <Typography
            variant="caption"
            data-testid="note-category"
            sx={{ fontWeight: 600 }}
          >
            {note.categoryName}
          </Typography>
        )}
        {isSystem && (
          <Chip
            label="System"
            size="small"
            data-testid="note-system-chip"
            color="default"
          />
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="note-timestamp"
          sx={{ marginLeft: 'auto !important' }}
        >
          {formatTimestamp(note.authoredAtUtc)}
        </Typography>
      </Stack>
      {note.body && (
        <Typography
          variant="body2"
          data-testid="note-body"
          sx={{ overflowWrap: 'anywhere' }}
        >
          {note.body}
        </Typography>
      )}
      {authorName && !isSystem && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="note-author"
          sx={{ overflowWrap: 'anywhere' }}
        >
          — {authorName}
        </Typography>
      )}
    </Box>
  );
};
